import type { ReactNode } from "react";
import { HAZARD_LABELS } from "@/lib/results";
import { describeExclusions, type ReportModel } from "@/lib/report";
import { ALIGNMENT_STATEMENT, BASIS_TEXT, HAZARD_BASIS } from "@/lib/report-basis";
import { SectionHeading } from "./ReportPage";

function Block({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="break-inside-avoid">
      <p className="font-semibold">{title}</p>
      <div className="mt-0.5 text-muted-foreground">{children}</div>
    </div>
  );
}

export function ReportBasis({ model }: { model: ReportModel }) {
  const caveatHazards = new Set(model.caveats.flatMap((c) => c.hazards));
  const assessed = new Set(model.assessedHazards);
  const withComputedFuture = new Set(model.hazardsWithComputedFuture);

  return (
    // Block + margin spacing for the same fragmentation reason as ReportSummary.
    <section className="space-y-3 text-xs leading-relaxed">
      <SectionHeading>Basis of preparation</SectionHeading>

      <Block title="Scope">{BASIS_TEXT.scope}</Block>
      <Block title="Scale">{BASIS_TEXT.scale}</Block>
      <Block title="Method">
        {BASIS_TEXT.method} {BASIS_TEXT.coordinates}
      </Block>

      <Block title="Indicators and data baselines">
        <ul className="space-y-1">
          {model.hazardsInScope.map((hazard) => {
            const basis = HAZARD_BASIS[hazard];
            return (
              <li key={hazard}>
                <span className="font-medium text-foreground">
                  {HAZARD_LABELS[hazard]}
                  {caveatHazards.has(hazard) && " †"}
                </span>
                {" — "}
                {basis.indicator}
                {basis.baselinePeriod && `; baseline period ${basis.baselinePeriod}`}.
                {!assessed.has(hazard) && ` ${BASIS_TEXT.notAssessed}`}
                {basis.notes.map((note) => ` ${note}`)}
                {withComputedFuture.has(hazard) && basis.futureSourceNote && ` ${basis.futureSourceNote}`}
              </li>
            );
          })}
        </ul>
      </Block>

      <Block title="Coverage and exclusions">
        <ul className="space-y-0.5">
          {model.sections.map((s) => (
            <li key={s.horizon}>
              {s.label}: {describeExclusions(s.statusCounts)}
            </li>
          ))}
        </ul>
      </Block>

      {model.caveats.length > 0 && (
        <Block title="Scenario caveats (†)">
          <ul className="space-y-0.5">
            {model.caveats.map((c) => (
              <li key={c.text}>
                {c.hazards.map((h) => HAZARD_LABELS[h]).join(", ")}: {c.text}
              </li>
            ))}
          </ul>
        </Block>
      )}

      <Block title="Alignment">{ALIGNMENT_STATEMENT}</Block>
    </section>
  );
}
